import { type CLASS_GROUPS } from "#constants/classOptions"
import {
  CLASS_GROUP_TO_SKILL_TYPE,
  type CoreSkillLevel,
  type DigitalSkillLevel,
  getStudentSkill,
  type SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import {
  type StudentConstructor,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"

import { addOneSemester, getCurrentSemester } from "./semesterHelpers"

/**
 * Resolving a student's skill level.
 *
 * `skillHistory` is the record of truth. It is keyed by semester, and
 * approving an evaluation writes the promoted level onto the semester that
 * FOLLOWS the eval's own (see `buildNextSemesterSkillPatch`), so a level is
 * only meaningful relative to a semester.
 *
 * The flat `coreSkillLevel` / `digitalSkillLevel` / `sculptureSkillLevel`
 * fields on the student doc are a legacy denormalized "current level" that
 * drifted out of agreement with the evals. They are never read: a student
 * with no history for a ladder has no level on it. The data stays on the
 * doc so a revert has something to read; `dbconversion/
 * backfillSkillHistoryFromFlatFields.ts` copies it into history.
 */

/** The ladder each skill type is measured on. */
export interface SkillLevelByType {
  [StudentSkillType.core]: CoreSkillLevel
  [StudentSkillType.digital]: DigitalSkillLevel
  [StudentSkillType.sculpture]: SculptureSkillLevel
}

/** The history a lookup reads. */
export type StudentSkillSource = Partial<
  Pick<StudentConstructor, "skillHistory">
>

/**
 * The student's level in one skill type as of `semester`: the newest
 * skillHistory entry at or before it.
 *
 * Defaults to the current semester. Pass a class's semester when the answer
 * should describe that class rather than today.
 */
export const getSkillLevelAsOf = <T extends StudentSkillType>(
  student: StudentSkillSource | null | undefined,
  skillType: T,
  semester: string = getCurrentSemester()
): SkillLevelByType[T] | "" =>
  // `skillType` carries the ladder; the history lookup widens to the union
  // of all three.
  (getStudentSkill(student?.skillHistory, skillType, semester) || "") as
    | SkillLevelByType[T]
    | ""

/**
 * Same lookup keyed by class group. CORE, PAINTING and PORTFOLIO all read
 * the core ladder; an unknown group falls back to core, matching
 * `TryoutProgressFormModal`.
 */
export const getSkillLevelForClassGroup = (
  student: StudentSkillSource | null | undefined,
  classGroup: CLASS_GROUPS | undefined,
  semester?: string
): CoreSkillLevel | DigitalSkillLevel | SculptureSkillLevel | "" =>
  getSkillLevelAsOf(
    student,
    (classGroup && CLASS_GROUP_TO_SKILL_TYPE[classGroup]) ??
      StudentSkillType.core,
    semester
  )

/** Core / digital / sculpture levels as of `semester`. */
export const getAllSkillLevelsAsOf = (
  student: StudentSkillSource | null | undefined,
  semester?: string
): { [T in StudentSkillType]: SkillLevelByType[T] | "" } => ({
  [StudentSkillType.core]: getSkillLevelAsOf(
    student,
    StudentSkillType.core,
    semester
  ),
  [StudentSkillType.digital]: getSkillLevelAsOf(
    student,
    StudentSkillType.digital,
    semester
  ),
  [StudentSkillType.sculpture]: getSkillLevelAsOf(
    student,
    StudentSkillType.sculpture,
    semester
  ),
})

/**
 * Prefill for the add/edit student form's three level dropdowns, resolved
 * from history as of now. The form fields share their names with the legacy
 * flat fields on the doc, so the doc must not be handed to the form as-is.
 */
export const getSkillLevelFormValues = (
  student: StudentSkillSource | null | undefined
): {
  coreSkillLevel: CoreSkillLevel | undefined
  digitalSkillLevel: DigitalSkillLevel | undefined
  sculptureSkillLevel: SculptureSkillLevel | undefined
} => {
  const levels = getAllSkillLevelsAsOf(student)
  return {
    coreSkillLevel: levels[StudentSkillType.core] || undefined,
    digitalSkillLevel: levels[StudentSkillType.digital] || undefined,
    sculptureSkillLevel: levels[StudentSkillType.sculpture] || undefined,
  }
}

/**
 * A level plus the promotion waiting behind it.
 *
 * Approving an evaluation records the promoted level against the FOLLOWING
 * semester, so between approval and the new semester starting a student has
 * been levelled up but is still sitting in a class at their old level. Both
 * halves matter to the front desk: `level` is what the class they are in
 * expects, `pending` is what the eval promoted them to.
 */
export interface SkillLevelWithPending<T extends StudentSkillType> {
  /** Level in effect for `semester`. */
  level: SkillLevelByType[T] | ""
  /** Level recorded for the next semester, when it differs from `level`. */
  pending: SkillLevelByType[T] | ""
  /** Semester `pending` takes effect. Empty when there is no promotion. */
  pendingSemester: string
}

/**
 * `getSkillLevelAsOf` plus the next semester's level when a promotion is
 * already recorded. `pending` is empty when the next semester has no entry,
 * repeats the current level, or does not exist in SEMESTER_DATES.
 */
export const getSkillLevelWithPending = <T extends StudentSkillType>(
  student: StudentSkillSource | null | undefined,
  skillType: T,
  semester: string = getCurrentSemester()
): SkillLevelWithPending<T> => {
  const level = getSkillLevelAsOf(student, skillType, semester)
  const nextSemester = addOneSemester(semester)
  const none = { level, pending: "", pendingSemester: "" } as const

  if (!nextSemester) return none

  // Only an entry ON the next semester is a promotion: `getSkillLevelAsOf`
  // carries the current level forward, so an equal value means no change.
  const next = getSkillLevelAsOf(student, skillType, nextSemester)
  if (!next || next === level) return none

  return { level, pending: next, pendingSemester: nextSemester }
}

/** Core / digital / sculpture, each with its pending promotion. */
export const getAllSkillLevelsWithPending = (
  student: StudentSkillSource | null | undefined,
  semester?: string
): {
  [T in StudentSkillType]: SkillLevelWithPending<T>
} => ({
  [StudentSkillType.core]: getSkillLevelWithPending(
    student,
    StudentSkillType.core,
    semester
  ),
  [StudentSkillType.digital]: getSkillLevelWithPending(
    student,
    StudentSkillType.digital,
    semester
  ),
  [StudentSkillType.sculpture]: getSkillLevelWithPending(
    student,
    StudentSkillType.sculpture,
    semester
  ),
})

/** Shared so a cell, a badge and a tooltip never disagree on the glyph. */
export const PROMOTION_ARROW = "→"

/**
 * One level for a compact surface (table cell, info row): `"O^ → R"` when a
 * promotion is waiting, `"O^"` otherwise, `"→ R"` when the only level on
 * record starts next semester. Codes, not labels — these sit in narrow
 * columns. Empty when the student has no level at all.
 */
export const formatSkillLevelWithPending = <T extends StudentSkillType>({
  level,
  pending,
}: SkillLevelWithPending<T>): string => {
  if (!level) return pending ? `${PROMOTION_ARROW} ${pending}` : ""
  return pending ? `${level} ${PROMOTION_ARROW} ${pending}` : level
}

/**
 * Student doc patch recording `level` for `semester`, as Firestore field
 * paths so the write merges at the leaf instead of replacing the whole
 * `skillHistory` map. The only way a level is written.
 */
export const buildSkillHistoryUpdate = (
  skillType: StudentSkillType,
  semester: string,
  level: string,
  evalId?: string
): Record<string, string> => ({
  [`skillHistory.${semester}.${skillType}`]: level,
  ...(evalId
    ? { [`skillHistory.${semester}.${skillType}EvalId`]: evalId }
    : {}),
})
